// 디렉토리 경로: client/src/components/reference-analysis/
// 파일명: ReferenceAnalysisModal.tsx
// 파일 코드의 역할/설명: 레퍼런스 분석 프롬프트 생성, 결과 파싱, 인사이트 등록 모달을 렌더링한다.

import { useCallback, useEffect } from 'react'
import type { LLMServiceType } from '@/data/llm-prompts'
import { useReferenceAnalysis } from '@/hooks/useReferenceAnalysis'
import {
  REFERENCE_ANALYSIS_SCORE_LABELS,
  makeReferenceAnalysisSummary,
} from '@/utils/referenceAnalysis'
import {
  REFERENCE_ANALYSIS_PANEL_SIZE,
  type InsightRegisterKind,
  type ReferenceAnalysisResult,
  type ReferenceAnalysisScoreKey,
  type ReferenceAnalysisTargetMode,
  type ReferenceDataQuality,
  type ReferenceItem,
} from '@/types'
import { ReferenceInsightRegisterPanel } from './ReferenceInsightRegisterPanel'
import styles from './ReferenceAnalysisModal.module.css'

interface ReferenceAnalysisModalProps {
  isOpen: boolean
  source: ReferenceItem | null
  onClose: () => void
}

const REFERENCE_ANALYSIS_PANEL_LABEL = `${REFERENCE_ANALYSIS_PANEL_SIZE.toLocaleString(
  'ko-KR'
)}명`

function formatDate(date: Date | string): string {
  const value = typeof date === 'string' ? new Date(date) : date
  return value.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function getScoreVerdict(result: ReferenceAnalysisResult) {
  const sorted = (Object.entries(result.scores) as [
    ReferenceAnalysisScoreKey,
    number,
  ][]).sort(([, a], [, b]) => b - a)
  const [primaryKey, primaryScore] = sorted[0]
  const structureScore = result.scores.structure
  const topicScore = result.scores.topic
  const delta = Math.abs(structureScore - topicScore)

  const comparison =
    delta <= 2
      ? {
          label: '구조/주제 균형',
          winner: 'balanced' as const,
          text: '구조와 주제가 거의 비슷합니다. 둘 다 재사용 가치가 있습니다.',
        }
      : structureScore > topicScore
        ? {
            label: '구조 우세',
            winner: 'structure' as const,
            text: '둘 중에는 주제보다 글의 순서, 전개, 프레임이 더 강합니다.',
          }
        : {
            label: '주제 우세',
            winner: 'topic' as const,
            text: '둘 중에는 구조보다 소재, 관점, 말할 주제가 더 강합니다.',
          }

  const primaryLabel = REFERENCE_ANALYSIS_SCORE_LABELS[primaryKey]
  const primaryText =
    primaryKey === 'structure'
      ? '결론적으로 이 글은 구조가 가장 강합니다.'
      : primaryKey === 'topic'
        ? '결론적으로 이 글은 주제가 가장 강합니다.'
        : `결론적으로 이 글은 ${primaryLabel}이 가장 강하고, 구조/주제 비교는 별도로 확인해야 합니다.`

  return {
    primaryKey,
    primaryLabel,
    primaryScore,
    structureScore,
    topicScore,
    delta,
    comparison,
    primaryText,
  }
}

// ============================================================================
// [v2] 가산 렌더 (전부 옵셔널·가드). v1 코어 렌더는 위에서 그대로 유지하고,
// 이 패널은 schemaVersion==='reference-analysis-v2'일 때만 RegisterPanel 앞에 추가한다.
// ============================================================================
const TARGET_MODE_LABEL: Record<ReferenceAnalysisTargetMode, string> = {
  reference: '레퍼런스 분석',
  draft: '내 초안 분석',
}

const DATA_QUALITY_LABEL: Record<ReferenceDataQuality, string> = {
  high: '높음',
  medium: '보통',
  low: '낮음',
}

const PERSONA_CARD_RENDER_CAP = 15

/** 숫자 가드: 0은 유효하므로 x && 금지. null/undefined/NaN만 배제. */
function isNum(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/**
 * 0~max 값을 0~100% 폭으로 격리 변환(스케일 혼동 방지의 단일 출구).
 * 0~10·0~1 바는 반드시 이 헬퍼를 거치고, 헬퍼 밖 리터럴 width를 두지 않는다.
 */
function barWidth(value: number, max: number): string {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return '0%'
  const pct = Math.max(0, Math.min(100, (value / max) * 100))
  return `${pct}%`
}

type ScaleTone = 'persona' | 'prob' | 'pct'

function ScaleBar({
  value,
  max,
  tone,
}: {
  value: number
  max: number
  tone: ScaleTone
}) {
  const fillClass =
    tone === 'persona'
      ? styles.barFillPersona
      : tone === 'prob'
        ? styles.barFillProb
        : styles.barFillPct
  return (
    <div className={styles.barTrack}>
      <i className={fillClass} style={{ width: barWidth(value, max) }} />
    </div>
  )
}

function PersonaScoreRow({
  label,
  value,
}: {
  label: string
  value: number | undefined
}) {
  if (!isNum(value)) return null
  return (
    <div className={styles.v2ScoreRow}>
      <span>{label}</span>
      <ScaleBar value={value} max={10} tone="persona" />
      <strong>{value}/10</strong>
    </div>
  )
}

export function ReferenceAnalysisV2Panel({
  result,
}: {
  result: ReferenceAnalysisResult
}) {
  const targetMode = result.targetMode ?? 'reference'
  const status = result.status
  const isDegraded = status === 'degraded'
  const isInsufficient = status === 'insufficient_input'

  const personaCards = Array.isArray(result.personaCards)
    ? result.personaCards.slice(0, PERSONA_CARD_RENDER_CAP)
    : []
  const personaCardOverflow =
    Array.isArray(result.personaCards) &&
    result.personaCards.length > PERSONA_CARD_RENDER_CAP
      ? result.personaCards.length - PERSONA_CARD_RENDER_CAP
      : 0

  const segments = Array.isArray(result.segmentBreakdown)
    ? result.segmentBreakdown
    : []
  const successDimensions = Array.isArray(result.successDimensions)
    ? result.successDimensions
    : []
  const expertPanel = Array.isArray(result.expertPanel) ? result.expertPanel : []
  const redteamVotes = Array.isArray(result.redteamVotes)
    ? result.redteamVotes
    : []
  const funnelStages = Array.isArray(result.funnelPrediction?.stages)
    ? result.funnelPrediction!.stages!
    : []
  const topLists = result.topLists
  const improvements = Array.isArray(result.improvements)
    ? result.improvements
    : []
  const attributionCaveats = Array.isArray(result.attributionCaveats)
    ? result.attributionCaveats
    : []
  const goalLeverGap = Array.isArray(result.goalLeverGap)
    ? result.goalLeverGap
    : []

  return (
    <section className={styles.v2Panel} aria-label="v2 확장 분석">
      <div className={styles.v2HeaderRow}>
        <h4>4. 확장 분석 (예측)</h4>
        <div className={styles.v2BadgeRow}>
          <span className={styles.v2BadgeMode}>{TARGET_MODE_LABEL[targetMode]}</span>
          {result.dataQuality && (
            <span className={styles.v2BadgeQuality}>
              데이터 품질 {DATA_QUALITY_LABEL[result.dataQuality]}
            </span>
          )}
        </div>
      </div>

      {(isInsufficient || isDegraded) && (
        <div
          className={`${styles.v2Banner} ${
            isInsufficient ? styles.v2BannerError : styles.v2BannerWarn
          }`}
        >
          <strong>
            {isInsufficient
              ? '입력이 부족해 분석이 제한적입니다 (insufficient_input).'
              : '일부 항목이 불완전합니다 (degraded).'}
          </strong>
          {result.statusReason && <p>{result.statusReason}</p>}
        </div>
      )}

      {isNum(result.goalAlignedScore) && (
        <div className={styles.v2Gauge}>
          <div className={styles.v2GaugeHead}>
            <span>목표 정렬 점수</span>
            <strong>{result.goalAlignedScore}/100</strong>
          </div>
          <ScaleBar value={result.goalAlignedScore} max={100} tone="pct" />
        </div>
      )}

      {result.audienceProfile?.oneLineReaderDef && (
        <div className={styles.v2ReaderDef}>
          <span>한 줄 독자 정의</span>
          <p>{result.audienceProfile.oneLineReaderDef}</p>
        </div>
      )}

      {result.hookAnalysis && (
        <div className={styles.v2Hook}>
          <div className={styles.v2HookHead}>
            <span>후킹 게이트</span>
            {result.hookAnalysis.hookType && (
              <em>{result.hookAnalysis.hookType}</em>
            )}
          </div>
          {result.hookAnalysis.firstLine && (
            <p className={styles.v2HookLine}>“{result.hookAnalysis.firstLine}”</p>
          )}
          {isNum(result.hookAnalysis.scrollStopScore) && (
            <PersonaScoreRow
              label="스크롤 정지"
              value={result.hookAnalysis.scrollStopScore}
            />
          )}
          {result.hookAnalysis.whyStopsOrFails && (
            <p className={styles.v2Muted}>{result.hookAnalysis.whyStopsOrFails}</p>
          )}
          {result.hookAnalysis.foldPoint && (
            <p className={styles.v2Muted}>{result.hookAnalysis.foldPoint}</p>
          )}
        </div>
      )}

      {result.riskAdjustedVerdict?.summary && (
        <div className={styles.v2Risk}>
          <span>리스크 반영 종합</span>
          <p>{result.riskAdjustedVerdict.summary}</p>
          {isNum(result.riskAdjustedVerdict.weightedConfidence) && (
            <ScaleBar
              value={result.riskAdjustedVerdict.weightedConfidence}
              max={1}
              tone="prob"
            />
          )}
          {Array.isArray(result.riskAdjustedVerdict.topRisks) &&
            result.riskAdjustedVerdict.topRisks.length > 0 && (
              <ul className={styles.v2RiskList}>
                {result.riskAdjustedVerdict.topRisks.map((risk, index) => (
                  <li key={`risk-${index}`}>{risk}</li>
                ))}
              </ul>
            )}
        </div>
      )}

      {(result.personaScores || segments.length > 0) && (
        <details className={styles.v2Details}>
          <summary>세그먼트 · 페르소나 평균</summary>
          <div className={styles.v2DetailsBody}>
            {result.personaScores && (
              <div className={styles.v2ScoreBlock}>
                <PersonaScoreRow
                  label="만족"
                  value={result.personaScores.satisfaction}
                />
                <PersonaScoreRow label="신뢰" value={result.personaScores.trust} />
                <PersonaScoreRow
                  label="수요 적합"
                  value={result.personaScores.demandFit}
                />
              </div>
            )}
            {segments.length > 0 && (
              <div className={styles.v2SegmentList}>
                {segments.map((segment, index) => (
                  <div
                    key={segment.segmentId ?? segment.cellId ?? `seg-${index}`}
                    className={styles.v2SegmentRow}
                  >
                    <div className={styles.v2SegmentHead}>
                      <strong>{segment.label ?? segment.segmentId ?? '세그먼트'}</strong>
                      {isNum(segment.ratioPct) && <span>{segment.ratioPct}%</span>}
                    </div>
                    {segment.avgScores && (
                      <div className={styles.v2ScoreBlock}>
                        <PersonaScoreRow
                          label="만족"
                          value={segment.avgScores.satisfaction}
                        />
                        <PersonaScoreRow
                          label="신뢰"
                          value={segment.avgScores.trust}
                        />
                        <PersonaScoreRow
                          label="수요 적합"
                          value={segment.avgScores.demandFit}
                        />
                      </div>
                    )}
                    {isNum(segment.intentPct) && (
                      <div className={styles.v2ScoreRow}>
                        <span>행동 의향</span>
                        <ScaleBar value={segment.intentPct} max={100} tone="pct" />
                        <strong>{segment.intentPct}%</strong>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </details>
      )}

      {personaCards.length > 0 && (
        <details className={styles.v2Details}>
          <summary>
            페르소나 카드 {personaCards.length}장
            {personaCardOverflow > 0 ? ` (외 ${personaCardOverflow}장 생략)` : ''}
          </summary>
          <div className={styles.v2PersonaGrid}>
            {personaCards.map((card, index) => (
              <article
                key={card.id ?? card.cellId ?? `persona-${index}`}
                className={styles.v2PersonaCard}
              >
                <header>
                  <strong>{card.name ?? card.id}</strong>
                  {card.segment && <span>{card.segment}</span>}
                </header>
                {card.profile && (
                  <p className={styles.v2Muted}>
                    {[
                      isNum(card.profile.age) ? `${card.profile.age}세` : null,
                      card.profile.gender,
                      card.profile.job,
                      card.profile.exposureContext,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
                {card.response?.firstImpression && (
                  <p>{card.response.firstImpression}</p>
                )}
                <div className={styles.v2ScoreBlock}>
                  <PersonaScoreRow
                    label="만족"
                    value={card.response?.satisfaction}
                  />
                  <PersonaScoreRow label="신뢰" value={card.response?.trustScore} />
                  <PersonaScoreRow
                    label="수요 적합"
                    value={card.response?.demandFit}
                  />
                </div>
                {isNum(card.response?.intentPct) && (
                  <div className={styles.v2ScoreRow}>
                    <span>의향</span>
                    <ScaleBar
                      value={card.response!.intentPct!}
                      max={100}
                      tone="pct"
                    />
                    <strong>{card.response!.intentPct}%</strong>
                  </div>
                )}
                {Array.isArray(card.response?.behaviorPrediction) &&
                  card.response!.behaviorPrediction!.length > 0 && (
                    <p className={styles.v2Muted}>
                      행동 예측: {card.response!.behaviorPrediction!.join(', ')}
                    </p>
                  )}
              </article>
            ))}
          </div>
        </details>
      )}

      {(result.funnelPrediction?.thumbStop3s || funnelStages.length > 0) && (
        <details className={styles.v2Details}>
          <summary>퍼널 예측</summary>
          <div className={styles.v2DetailsBody}>
            {result.funnelPrediction?.thumbStop3s && (
              <div className={styles.v2ScoreRow}>
                <span>
                  0~3초 썸스톱
                  {result.funnelPrediction.thumbStop3s.band
                    ? ` (${result.funnelPrediction.thumbStop3s.band})`
                    : ''}
                </span>
                {isNum(result.funnelPrediction.thumbStop3s.passProb) ? (
                  <>
                    <ScaleBar
                      value={result.funnelPrediction.thumbStop3s.passProb}
                      max={1}
                      tone="prob"
                    />
                    <strong>
                      {Math.round(
                        result.funnelPrediction.thumbStop3s.passProb * 100
                      )}
                      %
                    </strong>
                  </>
                ) : (
                  <strong>-</strong>
                )}
              </div>
            )}
            {funnelStages.length > 0 && (
              <ul className={styles.v2StageList}>
                {funnelStages.map((stage, index) => (
                  <li key={`stage-${index}`}>
                    <strong>
                      {stage.stage ?? `단계 ${index + 1}`}
                      {stage.band ? ` · ${stage.band}` : ''}
                    </strong>
                    {stage.dropPoint && <span> {stage.dropPoint}</span>}
                    {stage.population && (
                      <em className={styles.v2Muted}> ({stage.population})</em>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {result.funnelPrediction?.note && (
              <p className={styles.v2Muted}>{result.funnelPrediction.note}</p>
            )}
          </div>
        </details>
      )}

      {topLists &&
        ([
          ['희망 정보', topLists.wantedInfo],
          ['공통 불만', topLists.complaints],
          ['반응 깎는 요소', topLists.reactionKillers],
          ['고반응 세그먼트', topLists.highResponseSegments],
          ['저반응 세그먼트', topLists.lowResponseSegments],
        ] as Array<[string, string[] | undefined]>).some(
          ([, items]) => Array.isArray(items) && items.length > 0
        ) && (
          <details className={styles.v2Details}>
            <summary>핵심 리스트</summary>
            <div className={styles.v2TopLists}>
              {(
                [
                  ['희망 정보', topLists.wantedInfo],
                  ['공통 불만', topLists.complaints],
                  ['반응 깎는 요소', topLists.reactionKillers],
                  ['고반응 세그먼트', topLists.highResponseSegments],
                  ['저반응 세그먼트', topLists.lowResponseSegments],
                ] as Array<[string, string[] | undefined]>
              )
                .filter(([, items]) => Array.isArray(items) && items.length > 0)
                .map(([label, items]) => (
                  <div key={label} className={styles.v2TopListGroup}>
                    <h5>{label}</h5>
                    <ul>
                      {items!.map((item, index) => (
                        <li key={`${label}-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </details>
        )}

      {(successDimensions.length > 0 || expertPanel.length > 0) && (
        <details className={styles.v2Details}>
          <summary>전문가 패널 · 성공 차원</summary>
          <div className={styles.v2DetailsBody}>
            {successDimensions.length > 0 && (
              <div className={styles.v2DimList}>
                {successDimensions.map((dimension, index) => (
                  <div key={`dim-${index}`} className={styles.v2DimItem}>
                    <strong>
                      {dimension.name}
                      {dimension.band ? ` · ${dimension.band}` : ''}
                    </strong>
                    {dimension.rationale && <p>{dimension.rationale}</p>}
                  </div>
                ))}
              </div>
            )}
            {expertPanel.length > 0 && (
              <div className={styles.v2DimList}>
                {expertPanel.map((expert, index) => (
                  <div key={`expert-${expert.role}-${index}`} className={styles.v2DimItem}>
                    <strong>{expert.role}</strong>
                    {expert.finding && <p>{expert.finding}</p>}
                    {expert.leverMapping && (
                      <p className={styles.v2Muted}>{expert.leverMapping}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </details>
      )}

      {redteamVotes.length > 0 && (
        <details className={styles.v2Details}>
          <summary>레드팀 (자문 레이어 · 점수 비간섭)</summary>
          <div className={styles.v2RedteamList}>
            {redteamVotes.map((vote, index) => (
              <article key={`${vote.role}-${index}`} className={styles.v2RedteamItem}>
                <div className={styles.v2RedteamHead}>
                  <strong>{vote.role}</strong>
                  {vote.verdict && <span>{vote.verdict}</span>}
                </div>
                {vote.claim && <p>{vote.claim}</p>}
                {vote.counterexample && (
                  <p className={styles.v2Muted}>반례: {vote.counterexample}</p>
                )}
                {isNum(vote.confidence) && (
                  <div className={styles.v2ScoreRow}>
                    <span>확신도</span>
                    <ScaleBar value={vote.confidence} max={1} tone="prob" />
                    <strong>{Math.round(vote.confidence * 100)}%</strong>
                  </div>
                )}
                {vote.voteBasis && (
                  <p className={styles.v2Muted}>근거 유형: {vote.voteBasis}</p>
                )}
              </article>
            ))}
          </div>
        </details>
      )}

      {(improvements.length > 0 ||
        attributionCaveats.length > 0 ||
        goalLeverGap.length > 0 ||
        result.audienceDrift) && (
        <details className={styles.v2Details}>
          <summary>개선 · 귀인 주의 · 드리프트</summary>
          <div className={styles.v2DetailsBody}>
            {improvements.length > 0 && (
              <div className={styles.v2TopListGroup}>
                <h5>개선 방향</h5>
                <ul>
                  {improvements.map((item, index) => (
                    <li key={`imp-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {attributionCaveats.length > 0 && (
              <div className={styles.v2TopListGroup}>
                <h5>귀인 주의</h5>
                <ul>
                  {attributionCaveats.map((item, index) => (
                    <li key={`attr-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {goalLeverGap.length > 0 && (
              <div className={styles.v2TopListGroup}>
                <h5>목표 레버 미충족</h5>
                <ul>
                  {goalLeverGap.map((item, index) => (
                    <li key={`gap-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.audienceDrift &&
              (result.audienceDrift.currentAudience ||
                result.audienceDrift.intendedAudience ||
                result.audienceDrift.gap) && (
                <div className={styles.v2DimItem}>
                  <strong>오디언스 드리프트</strong>
                  {result.audienceDrift.currentAudience && (
                    <p>현재: {result.audienceDrift.currentAudience}</p>
                  )}
                  {result.audienceDrift.intendedAudience && (
                    <p>의도: {result.audienceDrift.intendedAudience}</p>
                  )}
                  {result.audienceDrift.gap && (
                    <p className={styles.v2Muted}>갭: {result.audienceDrift.gap}</p>
                  )}
                </div>
              )}
          </div>
        </details>
      )}

      <footer className={styles.v2Footer}>
        {result.panelLabel && <span>{result.panelLabel}</span>}
        {result.extrapolationNote && <p>{result.extrapolationNote}</p>}
        {result.methodologyNote && <p>{result.methodologyNote}</p>}
        {result.limitations && <p>{result.limitations}</p>}
        <p className={styles.v2Muted}>
          상태: {status ?? 'ok'}
          {result.dataQuality ? ` · 데이터 품질 ${DATA_QUALITY_LABEL[result.dataQuality]}` : ''}
        </p>
      </footer>
    </section>
  )
}

export function ReferenceAnalysisModal({
  isOpen,
  source,
  onClose,
}: ReferenceAnalysisModalProps) {
  const {
    promptText,
    rawResult,
    parsedResult,
    status,
    errorCode,
    errorMessage,
    isSaving,
    llmServices,
    preparePrompt,
    copyPrompt,
    openExternalLLM,
    setRawResult,
    parseResult,
    registerInsight,
    reset,
  } = useReferenceAnalysis()

  useEffect(() => {
    if (isOpen && source) {
      preparePrompt(source)
    }
  }, [isOpen, preparePrompt, source])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [onClose, reset])

  const handleRegister = useCallback(
    async (kind: InsightRegisterKind, value: string) => {
      if (!source || !parsedResult) return
      await registerInsight(kind, value, source, parsedResult)
    },
    [parsedResult, registerInsight, source]
  )

  if (!isOpen) return null

  const sortedScores = parsedResult
    ? (Object.entries(parsedResult.scores) as [
        ReferenceAnalysisScoreKey,
        number,
      ][]).sort(([, a], [, b]) => b - a)
    : []

  const summary = parsedResult ? makeReferenceAnalysisSummary(parsedResult) : null
  const verdict = parsedResult ? getScoreVerdict(parsedResult) : null

  return (
    <div className={styles.overlay} onMouseDown={handleClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reference-analysis-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div>
            <h3 id="reference-analysis-title">
              {REFERENCE_ANALYSIS_PANEL_LABEL} 가상 패널 분석
            </h3>
            <p>실제 설문/투표가 아닌 가상 시뮬레이션입니다.</p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="분석 모달 닫기"
          >
            ×
          </button>
        </header>

        {!source ? (
          <div className={styles.emptyState}>분석할 레퍼런스가 없습니다.</div>
        ) : (
          <div className={styles.body}>
            <section className={styles.sourceSummary}>
              <div>
                <span className={styles.sourceType}>
                  {source.sourceType === 'tracking' ? '트래킹' : '저장 글'}
                </span>
                <h4>{source.title}</h4>
                <p>{source.content.slice(0, 220)}</p>
              </div>
              <dl className={styles.sourceMeta}>
                <div>
                  <dt>생성일</dt>
                  <dd>{formatDate(source.createdAt)}</dd>
                </div>
                <div>
                  <dt>본문</dt>
                  <dd>{source.content.length}자</dd>
                </div>
                {source.metrics ? (
                  <>
                    <div>
                      <dt>조회</dt>
                      <dd>{source.metrics.views}</dd>
                    </div>
                    <div>
                      <dt>반응</dt>
                      <dd>
                        {source.metrics.likes} / {source.metrics.comments}
                      </dd>
                    </div>
                    <div>
                      <dt>성과점수</dt>
                      <dd>{source.performanceScore ?? '-'}</dd>
                    </div>
                  </>
                ) : (
                  <div>
                    <dt>성과</dt>
                    <dd>지표 없음</dd>
                  </div>
                )}
              </dl>
            </section>

            <section className={styles.promptSection}>
              <div className={styles.sectionHeader}>
                <h4>1. 프롬프트 복사</h4>
                <button
                  type="button"
                  onClick={() => copyPrompt(source)}
                  className={styles.primaryButton}
                >
                  프롬프트 복사
                </button>
              </div>

              <textarea
                value={promptText}
                readOnly
                className={styles.promptText}
                aria-label="레퍼런스 분석 프롬프트"
              />

              <div className={styles.llmButtons}>
                {(Object.keys(llmServices) as LLMServiceType[]).map((key) => {
                  const service = llmServices[key]
                  return (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => openExternalLLM(service.id)}
                    >
                      <span>{service.name}</span>
                      <small>{service.strength}</small>
                    </button>
                  )
                })}
              </div>
            </section>

            <section className={styles.parseSection}>
              <div className={styles.sectionHeader}>
                <h4>2. 분석 결과 붙여넣기</h4>
                <button
                  type="button"
                  onClick={() => parseResult(rawResult)}
                  disabled={!rawResult.trim()}
                  className={styles.primaryButton}
                >
                  결과 파싱
                </button>
              </div>

              <textarea
                value={rawResult}
                onChange={(event) => setRawResult(event.target.value)}
                className={styles.resultText}
                placeholder="외부 LLM이 반환한 JSON을 붙여넣으세요."
                aria-label="분석 결과 JSON"
              />

              {(errorMessage || status !== 'idle') && (
                <div
                  className={`${styles.status} ${
                    status.includes('error') ||
                    status === 'copy_failed' ||
                    status === 'auth_required' ||
                    status === 'popup_blocked'
                      ? styles.statusError
                      : styles.statusInfo
                  }`}
                >
                  {errorMessage ??
                    (status === 'copy_success'
                      ? '프롬프트를 복사했습니다.'
                      : status === 'parse_success'
                        ? '분석 결과를 파싱했습니다.'
                        : status === 'save_success'
                          ? '인사이트를 저장했습니다.'
                          : '진행 중입니다.')}
                  {errorCode && <span> 코드: {errorCode}</span>}
                </div>
              )}
            </section>

            {parsedResult && summary && (
              <section className={styles.analysisResult}>
                <div className={styles.sectionHeader}>
                  <h4>3. 분석 요약</h4>
                  <span className={styles.summaryBadge}>
                    Top {REFERENCE_ANALYSIS_SCORE_LABELS[summary.topScoreKey]}{' '}
                    {summary.topScore}
                  </span>
                </div>

                {verdict && (
                  <div className={styles.verdictPanel}>
                    <div className={styles.verdictPrimary}>
                      <span>핵심 판정</span>
                      <strong>
                        {verdict.primaryLabel}형 {verdict.primaryScore}
                      </strong>
                      <p>{verdict.primaryText}</p>
                    </div>

                    <div className={styles.verdictComparison}>
                      <div
                        className={`${styles.verdictScoreCard} ${
                          verdict.comparison.winner === 'structure'
                            ? styles.verdictWinner
                            : ''
                        }`}
                      >
                        <span>구조</span>
                        <strong>{verdict.structureScore}</strong>
                        <small>순서, 전개, 프레임</small>
                      </div>
                      <div
                        className={`${styles.verdictScoreCard} ${
                          verdict.comparison.winner === 'topic'
                            ? styles.verdictWinner
                            : ''
                        }`}
                      >
                        <span>주제</span>
                        <strong>{verdict.topicScore}</strong>
                        <small>소재, 관점, 메시지</small>
                      </div>
                      <div className={styles.verdictTextCard}>
                        <span>{verdict.comparison.label}</span>
                        <strong>
                          {verdict.delta <= 2
                            ? '차이 거의 없음'
                            : `${verdict.delta}점 차이`}
                        </strong>
                        <p>{verdict.comparison.text}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className={styles.scoreGrid}>
                  {sortedScores.map(([key, score]) => (
                    <div key={key} className={styles.scoreItem}>
                      <span>{REFERENCE_ANALYSIS_SCORE_LABELS[key]}</span>
                      <strong>{score}</strong>
                      <div className={styles.scoreBar}>
                        <i style={{ width: `${score}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.factorList}>
                  {parsedResult.winningFactors.map((factor, index) => (
                    <article
                      key={`${factor.key}-${index}`}
                      className={styles.factorItem}
                    >
                      <strong>
                        {factor.label} {factor.score}
                      </strong>
                      <p>{factor.evidence}</p>
                      <span>{factor.reusablePattern}</span>
                    </article>
                  ))}
                </div>

                {parsedResult.cautions.length > 0 && (
                  <div className={styles.cautionBox}>
                    <strong>주의</strong>
                    <ul>
                      {parsedResult.cautions.map((caution, index) => (
                        <li key={`caution-${index}`}>{caution}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {parsedResult.schemaVersion === 'reference-analysis-v2' && (
                  <ReferenceAnalysisV2Panel result={parsedResult} />
                )}

                <ReferenceInsightRegisterPanel
                  source={source}
                  result={parsedResult}
                  isSaving={isSaving}
                  onRegister={handleRegister}
                />
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ReferenceAnalysisModal
